import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    type JSX,
} from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { emitNotification } from "@impero-com/ui";
import { useForm, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";

import {
    ControlWithFieldsResources,
    ControlWithFieldsView,
    EditViewAction as ControlAction,
    DataSheetAssignmentsListDto,
} from "api/generated/model";
import { Field } from "components/fieldBuilder/models";
import { buildControlProgramViewUrl } from "org/control/controlProgram/urlUtils";

import { WebRoute } from "../../../routes";
import { useControlProgramQuery } from "../controlProgram/queries/useControlProgramQuery";
import { useEntityEditPermissions } from "../hooks/useEntityEditPermissions";

import { ControlViewForm } from "./ControlViewForm";
import {
    applyControlPolicies,
    toUpdateControlPayload,
    toControlViewFormState,
    useDataSheetAssignmentsForCurrentControl,
    ControlViewFormState,
} from "./formData";
import {
    useControlViewFormSchema,
    toRHFDefaultValues,
    rhfValuesToControlViewFormState,
    ControlViewFormSchema,
} from "./formSchema";
import { ControlPageWrapper, ControlSubmitType } from "./pageWrapper";
import { useControlSuspenseQuery } from "./queries/useControlSuspenseQuery";
import { useCPDatasheetAssignmentsQuery } from "./queries/useCPDatasheetAssignmentsQuery";
import { useUpdateControlMutation } from "./queries/useUpdateControlMutation";

interface UpdateControlViewProps {
    controlView: ControlWithFieldsView;
    controlResources: ControlWithFieldsResources;
    controlActions: ControlAction;
}
const defaultDataSheetAssignments: DataSheetAssignmentsListDto = {
    content: [],
    linkedResources: { controls: [], dataSheets: [] },
};

const UpdateControlView = ({
    controlView,
    controlResources,
    controlActions,
}: UpdateControlViewProps): JSX.Element => {
    const { t } = useTranslation();
    const { mutateAsync: updateControl } = useUpdateControlMutation();

    const controlId = controlView.id;
    const editingDisabled = !controlActions.edit;
    const { data } = useControlProgramQuery(controlView.controlProgramId);
    // TODO RFC 24, this has to change in favour of checks on the control itself.
    const hasFullAssignmentsAccess =
        data.content.actions.edit.accessRights ||
        data.content.actions.edit.content;
    const linkedEntities = data.linkedResources.entities;

    const baseFormState = useMemo(
        () =>
            applyControlPolicies(
                toControlViewFormState(controlView),
                controlResources.controlPolicies
            ),
        [controlView, controlResources.controlPolicies]
    );
    const triggersControls =
        "triggeredControlCount" in controlView
            ? controlView.triggeredControlCount > 0
            : false;
    // Live ref so the schema's DataSheet existence/grouping check sees fresh
    // assignments data once the query resolves (schema is built once, before
    // the controlProgramId-driven query has anything to return).
    const dataSheetAssignmentsRef = useRef<DataSheetAssignmentsListDto>(
        defaultDataSheetAssignments
    );
    const schema = useControlViewFormSchema(
        triggersControls,
        dataSheetAssignmentsRef
    );
    const rhfForm = useForm<ControlViewFormSchema>({
        resolver: zodResolver(schema),
        mode: "onBlur",
        defaultValues: toRHFDefaultValues(baseFormState),
    });

    const controlProgramId = useWatch({
        control: rhfForm.control,
        name: "controlProgramId",
    });
    // Save the original control program ID
    const [originalControlProgramId] = React.useState(controlProgramId);
    const controlTitle = useWatch({
        control: rhfForm.control,
        name: "title",
    });
    const sendImmediately = useWatch({
        control: rhfForm.control,
        name: "sendImmediately",
    });
    const assignments = useWatch({
        control: rhfForm.control,
        name: "controlAssignment",
    });
    const alteredTriggeredControlCount =
        Object.values(assignments.initialAssignment).length === 0 &&
        Object.values(controlView.stepGroup.initialStep.assignment).length !== 0
            ? controlView.triggeredControlCount
            : 0;

    const responsibleTasks = useWatch({
        control: rhfForm.control,
        name: "responsibleTasks",
    });
    const { canEditAnyLinkedEntity } = useEntityEditPermissions({
        entityIds: assignments.entityIds,
        linkedEntities,
    });
    const formDirty = rhfForm.formState.isDirty;
    // A user without edit access on the control can still update the
    // assignments of the entities they can edit
    const submitDisabled =
        (editingDisabled && !canEditAnyLinkedEntity) || !formDirty;

    // DataSheet assignments derived from RHF values
    const { data: dataSheetAssignmentsData } =
        useCPDatasheetAssignmentsQuery(controlProgramId);
    const dataSheetAssignments = useDataSheetAssignmentsForCurrentControl(
        responsibleTasks,
        dataSheetAssignmentsData ?? defaultDataSheetAssignments
    );
    useEffect(() => {
        dataSheetAssignmentsRef.current =
            dataSheetAssignmentsData ?? defaultDataSheetAssignments;
    }, [dataSheetAssignmentsData]);

    // Analytics tracking (not in schema — merged at submit time)
    const analyticsRef = useRef<Record<string, Field<unknown, string>[]>>({});
    const onAnalytics = useCallback(
        (suggestionId: string, fields: Field<unknown, string>[]) => {
            analyticsRef.current = {
                ...analyticsRef.current,
                [suggestionId]: [
                    ...(analyticsRef.current[suggestionId] ?? []),
                    ...fields,
                ],
            };
        },
        []
    );

    const validateForm = useCallback(
        () =>
            new Promise<ControlViewFormState | null>((resolve) => {
                void rhfForm.handleSubmit(
                    () => {
                        // getValues() keeps disabled fields; handleSubmit's data nulls them
                        const state = rhfValuesToControlViewFormState(
                            rhfForm.getValues(),
                            baseFormState
                        );
                        state.analytics = analyticsRef.current;
                        resolve(state);
                    },
                    (errors) => {
                        console.error("ControlViewForm validation errors", {
                            errors,
                            values: rhfForm.getValues(),
                        });
                        resolve(null);
                    }
                )();
            }),
        [rhfForm, baseFormState]
    );

    const handleSubmit = async (type: ControlSubmitType) => {
        const values = await validateForm();
        if (values === null) {
            emitNotification(
                "warning",
                t("CreateControl_Submit_ValidationError")
            );
            throw new Error("Validation error");
        }
        const data = toUpdateControlPayload(controlView, values);
        const response = await updateControl({
            data,
            controlId,
            params: {
                create_new_control_results: type === "send",
                send_immediately:
                    type === "send" && sendImmediately ? true : undefined,
            },
        });
        window.location.href = buildControlProgramViewUrl(controlProgramId!, {
            controlId: response.data.id,
        });
    };

    const handleCancel = () => {
        if (controlProgramId !== undefined && controlProgramId !== null) {
            window.location.href = buildControlProgramViewUrl(
                originalControlProgramId!,
                { controlId }
            );
        } else {
            window.location.href = `/${WebRoute.CONTROLS_ALL}`;
        }
    };

    return (
        <ControlPageWrapper
            submitDisabled={submitDisabled}
            titleLabel={controlTitle}
            submitLabel={t("CreateControl_UpdateControl")}
            controlId={controlId}
            controlProgramId={controlProgramId}
            formDirty={formDirty}
            onSubmitClick={handleSubmit}
            onCancel={handleCancel}
            validateForm={validateForm}
            pageType="update"
            sendImmediately={sendImmediately}
            alteredTriggeredControlCount={alteredTriggeredControlCount}
        >
            <ControlViewForm
                disabled={editingDisabled}
                control={rhfForm.control}
                getValues={rhfForm.getValues}
                setValue={rhfForm.setValue}
                trigger={rhfForm.trigger}
                onAnalytics={onAnalytics}
                controlResources={controlResources}
                controlId={controlId}
                dataSheetAssignments={dataSheetAssignments}
                notificationRecipients={
                    controlView.stepGroup.notificationRecipients
                }
                initialTriggeredByControlId={controlView.triggeredBy ?? null}
                linkedEntities={linkedEntities}
                hasFullAssignmentsAccess={hasFullAssignmentsAccess}
            />
        </ControlPageWrapper>
    );
};

const UpdateControlPage = (): JSX.Element => {
    const { id: controlId } = useParams();
    if (!controlId) {
        throw new Error("Control ID should be defined");
    }
    const { data, status, error } = useControlSuspenseQuery(controlId);

    switch (status) {
        case "error":
            return <LoggedInHTTPError code={error?.response?.status} />;
        case "success":
            return (
                <UpdateControlView
                    controlActions={data.content.actions}
                    controlResources={data.linkedResources}
                    controlView={data.content.resource}
                />
            );
        default:
            assertNever(status);
    }
};

export { UpdateControlPage, UpdateControlView };
